import React, { useState, useEffect } from "react";
import logo from "assets/img/logo.png";
import axios from "axios";
import { useToast } from "@chakra-ui/react";
import config from "config";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { translateBackendError } from "utils/errorUtils";

// Chakra imports
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  keyframes,
} from "@chakra-ui/react";
import MufynButton from "components/Button";

// Animations (same as SignIn)
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Utiliser la langue du navigateur pour les pages publiques
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    
    if (!i18n.language || i18n.language === 'en') {
      i18n.changeLanguage(getBrowserLanguage());
    }
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!email) {
      toast({
        title: t("forgot_password.error.title"),
        description: t("forgot_password.error.fill_email"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${config.apiUrl}/request-password-reset`, { email });
      
      if (!response.data || !response.data.message) {
        throw new Error("Password reset request failed - no response");
      }
      
      setEmailSent(true);
      toast({
        title: t("forgot_password.success.title"),
        description: t("forgot_password.success.description"),
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: t("forgot_password.error.title"),
        description: error.response?.data?.message
          ? translateBackendError(error.response.data.message, t, "forgot_password.error.server_error")
          : t("forgot_password.error.server_error"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      overflow="hidden"
      bg="#0a0d14"
    >
      {/* Animated Background */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.15) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        
        {/* Floating orbs */}
        <Box
          position="absolute"
          top="10%"
          left="5%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        
        {/* Grid pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />

        {/* Animated lines */}
        <Box
          position="absolute"
          top="30%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
      </Box>

      {/* Main Content */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        align="center"
        justify="center"
        p={{ base: 4, md: 8 }}
      >
        <Box
          as="form"
          onSubmit={handleSubmit}
          w="100%"
          maxW="480px"
          animation={`${slideUp} 0.8s ease-out`}
        >
          {/* Glass Card */}
          <Box
            position="relative"
            p={{ base: 8, md: 12 }}
            borderRadius="24px"
            bg="linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.6) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid rgba(34, 197, 94, 0.15)"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              h: "1px",
              background: "linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)",
            }}
          >
            {/* Logo & Header */}
            <Flex direction="column" align="center" mb={8}>
              <Box position="relative" p={4} mb={0}>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  w="150px"
                  h="150px"
                  borderRadius="full"
                  bg="radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, rgba(34, 197, 94, 0.1) 50%, transparent 70%)"
                  filter="blur(20px)"
                  animation={`${pulse} 3s ease-in-out infinite`}
                />
                <img
                  src={logo}
                  width="120px"
                  alt="logo"
                  style={{ position: "relative", zIndex: 1 }}
                />
              </Box>

              <Heading
                color="white"
                fontSize={{ base: "28px", md: "36px" }}
                fontWeight="bold"
                bgGradient="linear(to-r, #fff, #4ade80, #fff)"
                bgClip="text"
                backgroundSize="200% auto"
                animation={`${gradientMove} 5s linear infinite`}
              >
                {t("forgot_password.title")}
              </Heading>

              <Text mt={3} color="gray.400" fontSize="md" textAlign="center">
                {t("forgot_password.subtitle")}
              </Text>
            </Flex>

            {/* Form Fields */}
            {!emailSent ? (
              <Box>
                {/* Email */}
                <FormControl mb={6}>
                  <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                    {t("forgot_password.email")}
                  </FormLabel>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    autoComplete="email"
                    placeholder={t("forgot_password.your_email")}
                    size="lg"
                    bg="rgba(15, 20, 30, 0.6)"
                    border="1px solid rgba(255, 255, 255, 0.1)"
                    borderRadius="16px"
                    color="white"
                    h="56px"
                    fontSize="md"
                    _placeholder={{ color: "gray.500" }}
                    _hover={{
                      border: "1px solid rgba(34, 197, 94, 0.3)",
                      bg: "rgba(15, 20, 30, 0.8)",
                    }}
                    _focus={{
                      border: "1px solid rgba(34, 197, 94, 0.6)",
                      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
                      bg: "rgba(15, 20, 30, 0.8)",
                    }}
                    transition="all 0.3s ease"
                  />
                </FormControl>

                {/* Submit Button */}
                <MufynButton
                  type="submit"
                  size="xl"
                  variant="primary"
                  fullWidth
                  isLoading={isLoading}
                  withRipple
                >
                  {t("forgot_password.send_link")}
                </MufynButton>
              </Box>
            ) : (
              <Box textAlign="center">
                <Text color="gray.300" fontSize="md" mb={6}>
                  {t("forgot_password.success.description")}
                </Text>
              </Box>
            )}

            {/* Footer Link */}
            <Flex justify="center" mt={8}>
              <Link
                as={RouterLink}
                to="/auth/signin"
                color="#4ade80"
                fontSize="sm"
                fontWeight="semibold"
                _hover={{
                  color: "#22c55e",
                  textDecoration: "none",
                }}
                transition="color 0.2s ease"
              >
                ← {t("forgot_password.back_to_login")}
              </Link>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default ForgotPassword;
